import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/auth/requireAdmin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, MoreHorizontal, Play, Database } from "lucide-react";
import { toast } from "sonner";
import {
  Button, Card, CardContent, PageContent, Input,
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem, DropdownMenuSeparator,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "beproduct-ui";
import { api, isApiError } from "@/lib/api";
import { PageHeader } from "@/components/navigation/page-header";
import { JobsTable, type JobWithLastRun } from "@/components/jobs-table";

interface TenantJobsResponse {
  tenant: { id: string; name: string };
  jobs: JobWithLastRun[];
}

export const Route = createFileRoute("/_authed/$companySlug/jobs/")({
  beforeLoad: ({ context, params }) => { requireAdmin(context, params.companySlug); },
  component: CompanyJobsPage,
});

function CompanyJobsPage() {
  const { company } = Route.useRouteContext();
  const { companySlug } = Route.useParams();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [runAllOpen, setRunAllOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseConfirmation, setReleaseConfirmation] = useState("");
  const [inspectionConfirmed, setInspectionConfirmed] = useState(false);
  const [preparation, setPreparation] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["tenant", company.id],
    queryFn: () => api<TenantJobsResponse>(`/api/tenants/${company.id}`),
    refetchInterval: 5_000,
  });
  const maintenance = useQuery({
    queryKey: ["tb-rebuild-maintenance", company.id],
    queryFn: () => api<{ maintenance: boolean; remainingRuns: number; phase: string; canRetry: boolean }>(
      `/api/tenants/${company.id}/tb-rebuild-maintenance`,
    ),
    refetchInterval: 3_000,
  });
  const prepare = useMutation({
    mutationFn: async () => {
      const endpoint = `/api/tenants/${company.id}/prepare-tb-rebuild`;
      const maintenanceEndpoint = `/api/tenants/${company.id}/tb-rebuild-maintenance`;
      // This remains a single operator action. The server cancels queued
      // work immediately; a running ETL step may need a few minutes to exit.
      // Poll status with short HTTP requests instead of one long request
      // that might time out behind a reverse proxy.
      const deadline = Date.now() + 10 * 60_000;
      for (;;) {
        try {
          return await api<{ droppedTables: string[]; rearmedRows: Record<string, number> }>(
            endpoint,
            { method: "POST", body: JSON.stringify({ confirmation }) },
          );
        } catch (err) {
          if (!isApiError(err) || err.status !== 409 ||
              (err.body as { error?: string } | null)?.error !== "maintenance_draining") {
            throw err;
          }
          setPreparation("Stopping this company's workflows before rebuilding TB tables…");
          while (Date.now() < deadline) {
            await new Promise<void>(resolve => setTimeout(resolve, 2_000));
            const state = await api<{ maintenance: boolean; remainingRuns: number; phase: string; canRetry: boolean }>(maintenanceEndpoint);
            if (!state.maintenance || state.phase !== "draining") {
              throw new Error("Prepare is no longer in the safe draining phase. Inspect the warehouse before retrying.");
            }
            if (state.remainingRuns === 0) break;
          }
          if (Date.now() >= deadline) {
            throw new Error(
              "Some workflows are still running. No TB tables were dropped. " +
              "Maintenance remains active for this company; use Retry Prepare rebuild when the runs stop.",
            );
          }
          // The next POST completes the actual reset, without another click.
        }
      }
    },
    onSuccess: (result) => {
      const count = Object.values(result.rearmedRows).reduce((sum, n) => sum + n, 0);
      setPreparation(`Prepared: ${result.droppedTables.length} tables dropped; ${count} source rows re-armed. Rebuild TB from cache reuses raw Attributes/Apps. The first Tracking step after Prepare automatically forces a full view refresh, including during ordinary Run All.`);
      setConfirmOpen(false);
      setConfirmation("");
      qc.invalidateQueries({ queryKey: ["tb-rebuild-maintenance", company.id] });
      qc.invalidateQueries({ queryKey: ["tenant", company.id] });
      toast.success("TB rebuild prepared");
    },
    onError: (err: Error & { body?: { message?: string; error?: string } }) => {
      const message = err.body?.message ?? err.message;
      setPreparation(message);
      qc.invalidateQueries({ queryKey: ["tb-rebuild-maintenance", company.id] });
      toast.error(message);
    },
  });
  const releaseMaintenance = useMutation({
    mutationFn: () => api<{ ok: boolean }>(
      `/api/tenants/${company.id}/release-tb-rebuild-maintenance`,
      { method: "POST", body: JSON.stringify({
        confirmation: releaseConfirmation, inspectedWarehouse: inspectionConfirmed,
      }) },
    ),
    onSuccess: () => {
      setReleaseOpen(false);
      setReleaseConfirmation("");
      setInspectionConfirmed(false);
      setPreparation("Maintenance released after operator inspection. Confirm TB table state before running workflows.");
      qc.invalidateQueries({ queryKey: ["tb-rebuild-maintenance", company.id] });
      toast.success("TB maintenance released");
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const rebuild = useMutation({
    mutationFn: () => api<{
      workflows: number;
      queued: number;
      skipped: { jobId: string; reason: string }[];
      failed: { jobId: string; reason: string }[];
    }>(`/api/tenants/${company.id}/rebuild-tb-from-cache`, { method: "POST" }),
    onSuccess: (result) => {
      toast[result.failed.length ? "warning" : "success"](
        `Queued ${result.queued} of ${result.workflows} workflows for cached TB rebuild` +
        (result.skipped.length ? `; ${result.skipped.length} already active` : "") +
        (result.failed.length ? `; ${result.failed.length} enqueue failed` : ""),
      );
      qc.invalidateQueries({ queryKey: ["tenant", company.id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const runAll = useMutation({
    mutationFn: () => api<{
      workflows: number;
      queued: number;
      skipped: { jobId: string; reason: string }[];
      failed: { jobId: string; reason: string }[];
    }>(`/api/tenants/${company.id}/jobs/run-all`, { method: "POST" }),
    onSuccess: (result) => {
      setRunAllOpen(false);
      toast[result.failed.length ? "warning" : "success"](
        `Queued ${result.queued} of ${result.workflows} workflows` +
        (result.skipped.length ? `; ${result.skipped.length} already active` : "") +
        (result.failed.length ? `; ${result.failed.length} enqueue failed` : ""),
      );
      qc.invalidateQueries({ queryKey: ["tenant", company.id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <PageContent>
      <div className="pt-4 pb-8 md:pt-6 space-y-6">
        <PageHeader title="Workflows" actions={
          <div className="flex items-center gap-2">
            <Button asChild>
              <Link to="/$companySlug/jobs/new" params={{ companySlug }}>
                <Plus className="size-4" /> Add workflow
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline"><MoreHorizontal className="size-4" /> Actions</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={maintenance.data?.maintenance} onSelect={() => setRunAllOpen(true)}>
                  <Play className="mr-2 size-4" /> Run all workflows
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled={maintenance.data?.maintenance || prepare.isPending}
                  onSelect={() => setConfirmOpen(true)} className="text-destructive">
                  <Database className="mr-2 size-4" /> Prepare full TB rebuild
                </DropdownMenuItem>
                <DropdownMenuItem disabled={maintenance.data?.maintenance || rebuild.isPending ||
                  !data?.jobs.some(job => job.enabled)}
                  onSelect={() => rebuild.mutate()}>
                  <Database className="mr-2 size-4" /> Rebuild TB from cache
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        } />
        {preparation && <div role="status" className="rounded border p-3 text-sm">{preparation}</div>}
        {maintenance.data?.maintenance && (
          <div role="status" className="rounded-lg border p-4 space-y-2">
            <div className="font-medium">TB rebuild maintenance active</div>
            <p className="text-sm text-muted-foreground">
              {maintenance.data.phase === "draining"
                ? maintenance.data.remainingRuns > 0
                  ? `${maintenance.data.remainingRuns} run(s) are still stopping. No TB tables have been dropped yet.`
                  : "All workflow runs have stopped; retry Prepare to finish the reset."
                : "Maintenance remains active. Inspect the warehouse before retrying Prepare."}
            </p>
            {!prepare.isPending && maintenance.data.canRetry && (
              <Button size="sm" variant="outline" onClick={() => setConfirmOpen(true)}>
                Retry Prepare rebuild
              </Button>
            )}
            {maintenance.data.phase === "inspection_required" && (
              <Button size="sm" variant="outline" onClick={() => setReleaseOpen(true)}>
                Release maintenance after inspection
              </Button>
            )}
          </div>
        )}
        {error ? (
          <Card><CardContent className="p-6 text-destructive">Failed to load workflows.</CardContent></Card>
        ) : isLoading || !data ? (
          <Card><CardContent className="p-6 text-muted-foreground">Loading…</CardContent></Card>
        ) : (
          <JobsTable companySlug={companySlug} jobs={data.jobs}
            invalidateKey={["tenant", company.id] as const} />
        )}
      </div>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Prepare full TB rebuild · {company.name}</DialogTitle>
            <DialogDescription>
              This deletes rebuildable tb_* tables and prepares source data for rebuilding.
              Raw be_* data, tb_index and notes, images, files, embeddings, and fetch
              quarantine are preserved.
              Queued workflows will be cancelled; running workflows stop after their
              current step. Reports may be incomplete until rebuilt.
              Prepare does not run workflows. Afterwards, run Rebuild TB from cache.
              The next Tracking run will perform a full API refresh.
              Back up the warehouse before proceeding.
            </DialogDescription>
          </DialogHeader>
          <label className="text-sm" htmlFor="confirm-rebuild">Type {companySlug} to confirm</label>
          <Input id="confirm-rebuild" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={confirmation !== companySlug || prepare.isPending}
              onClick={() => prepare.mutate()}>
              {prepare.isPending ? "Preparing…" : "Prepare rebuild"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={releaseOpen} onOpenChange={setReleaseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Release TB maintenance · {company.name}</DialogTitle>
            <DialogDescription>
              This is for an interrupted or unconfirmed Prepare only. Inspect the warehouse
              first: its TB tables may have been dropped or partly rebuilt. Releasing
              maintenance does not restore tables, quarantine, or source flags.
              Do not use this while a reset or workflow is running.
            </DialogDescription>
          </DialogHeader>
          <label className="text-sm" htmlFor="release-confirm">Type {companySlug} after warehouse inspection</label>
          <Input id="release-confirm" value={releaseConfirmation}
            onChange={(e) => setReleaseConfirmation(e.target.value)} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={inspectionConfirmed}
              onChange={(e) => setInspectionConfirmed(e.target.checked)} />
            I inspected the warehouse and accept the current TB table state
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={releaseConfirmation !== companySlug ||
              !inspectionConfirmed || releaseMaintenance.isPending}
              onClick={() => releaseMaintenance.mutate()}>
              {releaseMaintenance.isPending ? "Releasing…" : "Release maintenance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={runAllOpen} onOpenChange={setRunAllOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run all workflows · {company.name}</DialogTitle>
            <DialogDescription>
              Queue all {data?.jobs.filter(job => job.enabled).length ?? 0} enabled workflows using the same mechanism as Run now.
              An existing enabled Database Setup workflow is submitted first. Disabled workflows are excluded.
              Actual execution order across queues is not guaranteed; track status and logs in the table.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRunAllOpen(false)}>Cancel</Button>
            <Button disabled={runAll.isPending || !data?.jobs.some(job => job.enabled)} onClick={() => runAll.mutate()}>
              {runAll.isPending ? "Starting…" : "Run all"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContent>
  );
}
